<template>
  <v-app>
    <!-- 🔴 Above everything, including a module and a drawer (D34). An operator who forgets they are
         assisting is how this feature turns into an incident, so it is drawn before the chrome is
         even decided — a signed-out shell holding an assist would be stranger still. -->
    <AssistBanner :assisting="session.assisting" @stop="session.stopAssisting()" />
    <!-- 🔴 A RESET LINK IS ANSWERED BEFORE ANYTHING ELSE IS DECIDED (D122, PNK-143).
         Above the session entirely, and deliberately: somebody following `/r/<token>`
         may be signed out, may still be signed in on this tab, or may be waiting on a
         `resolving` boot that is slow or failing — and in all three cases the screen
         they asked for is the same one. It is also not a module, so `publicPath` would
         never have matched it; it is shell chrome, exactly like the sign-in screen. -->
    <!-- 🔴 An MCP client's sign-in and consent (D133): shell chrome, drawn whatever the session status — the
         page itself shows the sign-in when there is no session, and resumes the flow when there is one. -->
    <OAuthAuthorize v-if="oauthAuthorize" />
    <OAuthConsent v-else-if="oauthConsent" />
    <ResetPassword v-else-if="resetToken" :token="resetToken" />
    <!-- 🔴 AN INVITATION LINK IS ANSWERED THE SAME WAY (D126): `/i/<token>` is the shell's own page,
         drawn whether the person is signed out, signed in, or still resolving — the view reads the
         session status and decides which of its three faces applies. -->
    <Invitation v-else-if="invitationToken" :token="invitationToken" :session-status="session.status" />
    <!-- 🔴 PNK-253: an onboarding START link (a QR, a copy, WhatsApp) — answered the same way, whatever the session. It
         grants nothing: the page only asks Pinksy to email the person their own onboarding link. -->
    <OnboardingStart v-else-if="onboardStartToken" :token="onboardStartToken" />
    <NoAccess v-else-if="session.status === 'no-access'" />
    <template v-else-if="session.status === 'ready'">
      <TopBar />
      <Sidebar />
      <!-- The thin strip at the foot of every signed-in screen: terms, which build this is, a NEW
           VERSION pill when one is waiting, the theme toggle and help (D42). Chrome, like the bar
           and the drawer — a module never draws it and never has to know it is there. -->
      <!-- 🔴 Not on a phone: there it is a row in the nav drawer (BottomRail.vue, SidebarFooter.vue). -->
      <BottomRail v-if="!mobile" />
      <!-- Only rendered at "/" — hosted modules mount straight onto
           document.body (outside this tree) for every other path, so this
           never competes with a module for the same screen real estate. -->
      <!-- 🔴 The passkey screen is the shell's own, for the reason Security.vue gives:
           the shell is the only thing holding this tab's credential, and the ceremony
           is performed with it. A module is a separate bundle and must not be handed it. -->
      <v-main v-if="showSecurity">
        <Security />
      </v-main>
      <v-main v-else-if="showAppHome">
        <AppHome />
      </v-main>
    </template>
    <!-- A public path (the link, nav/publicPaths.ts) signed-out: nothing of
         ours — the module mounted there owns the whole screen until the
         session it establishes draws the chrome. -->
    <template v-else-if="publicPath" />
    <!-- 🔴 `signing-in` BELONGS HERE, and leaving it out was a real defect (PNK-143).
         Devin: *"if I have already typed in the password, the email address, and I hit
         reset my password, after it tries, it should at least remember my email address."*
         It could not. During `signing-in` this branch matched nothing, so Login UNMOUNTED
         mid-attempt and its `email`/`password` refs were destroyed with it; a failure then
         mounted a brand-new, empty form. Login was always written for this — it carries
         `busy = status === "signing-in"` and a `:loading` button — it simply never got the
         chance to render it. Keeping it mounted is what makes the field survive. -->
    <Login v-else-if="session.status === 'signed-out' || session.status === 'signing-in' || session.status === 'error'" />
    <!-- single-spa mounts the tenant's modules straight onto document.body
         (single-spa-vue's default target — no host container needed);
         modules pad themselves below via --pinksy-header-height. -->
    <div v-else class="standalone-loading">
      <OrbitalLoader />
    </div>
  </v-app>
</template>

<script lang="ts">
import { computed, defineComponent, onMounted, onUnmounted, ref, watch } from "vue";
import { useDisplay, useTheme } from "vuetify";
import { installPrintTheme } from "./printTheme";
import { useSessionStore } from "@/stores/session";
import { HOST_ASSIST_EVENT, type HostAssistRequest } from "@/state/globalState";
import { isPublicPath } from "@/nav/publicPaths";
import { closeNav, rail } from "./navState";
import Sidebar from "./Sidebar.vue";
import OrbitalLoader from "./OrbitalLoader.vue";
import TopBar from "./TopBar.vue";
import BottomRail from "./BottomRail.vue";
import AssistBanner from "./AssistBanner.vue";
import AppHome from "./views/AppHome.vue";
import Login from "./views/Login.vue";
import ResetPassword from "./views/ResetPassword.vue";
import OAuthAuthorize from "./views/OAuthAuthorize.vue";
import OAuthConsent from "./views/OAuthConsent.vue";
import Invitation from "./views/Invitation.vue";
import OnboardingStart from "./views/OnboardingStart.vue";
import Security from "./views/Security.vue";
import { OAUTH_AUTHORIZE_ROUTE, OAUTH_CONSENT_ROUTE, SECURITY_ROUTE, invitationTokenOf, onboardStartTokenOf, resetTokenOf } from "@/nav/shellRoutes";
import NoAccess from "./views/NoAccess.vue";
import { APP_HOME_PAGE, APP_MODULES } from "@/app";

/** The CSS custom properties every hosted module lays itself out by (build plan §7). */
export const NAV_WIDTH_VAR = "--pinksy-nav-width";
export const HEADER_HEIGHT_VAR = "--pinksy-header-height";
export const FOOTER_HEIGHT_VAR = "--pinksy-footer-height";

/**
 * Matches TopBar's `density="compact"` app-bar height exactly (Vuetify's compact app-bar is 48px) — the single
 * source of truth every hosted module's `padding-top: var(--pinksy-header-height)` reads. Zero while there is no
 * bar: a module on a public path, signed-out, owns the screen from its top edge.
 */
export const HEADER_HEIGHT_WITH_BAR = "48px";
export const HEADER_HEIGHT_WITHOUT_BAR = "0px";
/**
 * 🔴 The bottom rail's own height, published the same way the header's is (`--pinksy-rail-height`).
 *
 * A module reserves the strip with `padding-bottom: var(--pinksy-rail-height)` and never has to know
 * what is drawn in it. Zero while there is no rail — signed out, or a module on a public path owning
 * the screen to its bottom edge — so nothing pads for chrome that is not there.
 */
export const RAIL_HEIGHT_VAR = "--pinksy-rail-height";
export const RAIL_HEIGHT_WITH_RAIL = "26px";
export const RAIL_HEIGHT_WITHOUT_RAIL = "0px";
/**
 * 🔴 The footer a module reserves is the rail AND the home indicator under it (hardening: with
 * `viewport-fit=cover` the rail's icons sat under an iPhone's home indicator, and the phone tab bar
 * padded the same inset a second time above the rail). The rail pads itself by the inset (BottomRail),
 * and every module that lays out by `--pinksy-footer-height` gets the whole strip.
 */
export const FOOTER_HEIGHT_WITH_RAIL = `calc(${RAIL_HEIGHT_WITH_RAIL} + env(safe-area-inset-bottom))`;
/**
 * The assist banner's own height, added to the header offset while one is on screen (D34) — so a
 * hosted module drops below it rather than having its first row covered by the loudest bar on the
 * page. `styles/standalone.css` reads the same variable for the banner itself.
 */
export const ASSIST_BANNER_HEIGHT_PX = 40;
export const ASSIST_BANNER_HEIGHT_VAR = "--pinksy-assist-banner-height";

export default defineComponent({
  name: "App",
  components: { Sidebar, TopBar, BottomRail, AssistBanner, AppHome, OrbitalLoader, Login, NoAccess, ResetPassword, Invitation, OnboardingStart, Security, OAuthAuthorize, OAuthConsent },
  setup() {
    const session = useSessionStore();
    const path = ref(window.location.pathname);

    // `mobileBreakpoint: 768` — plugins/vuetify.ts. The nav is an OVERLAY
    // below it (Sidebar.vue), so it occupies no layout width and every hosted
    // module gets the full viewport.
    const { mobile } = useDisplay();
    // Paper is light: a dark theme is turned light for the length of a print, and put back (printTheme.ts).
    const theme = useTheme();
    let stopPrintTheme: (() => void) | null = null;
    onMounted(() => {
      stopPrintTheme = installPrintTheme({ isDark: () => theme.global.current.value.dark, change: (name) => theme.change(name) });
    });
    onUnmounted(() => stopPrintTheme?.());
    watch(
      [rail, mobile],
      ([collapsed, isMobile]) => {
        publishNavWidth(isMobile ? "0px" : collapsed ? "56px" : "230px");
        // Rotating a phone to a tablet width leaves the temporary drawer's
        // state behind; a permanent drawer is already showing, so a stale
        // "open" would only re-appear as an overlay on the way back.
        if (!isMobile) closeNav();
      },
      { immediate: true },
    );

    const showAppHome = computed(() => path.value === "/" && APP_HOME_PAGE.enabled);
    /**
     * The two paths the SHELL draws itself: "How you sign in", and the `/r/<token>` a reset
     * mail lands on. Both are declared in `nav/shellRoutes.ts` rather than here, because
     * `boot.ts` has to know them too — it used to redirect a signed-in visitor off both of
     * them to the home route, silently (PNK-145).
     *
     * Not `publicPaths.ts`: that file answers whether a MODULE is mounted on a prefix served
     * signed-out. Nothing is mounted on these and nothing should be.
     */
    const showSecurity = computed(() => path.value === SECURITY_ROUTE);
    const oauthAuthorize = computed(() => path.value === OAUTH_AUTHORIZE_ROUTE);
    const oauthConsent = computed(() => path.value === OAUTH_CONSENT_ROUTE);
    const resetToken = computed(() => resetTokenOf(path.value));
    const invitationToken = computed(() => invitationTokenOf(path.value));
    const onboardStartToken = computed(() => onboardStartTokenOf(path.value));
    // The link path (P-09): served with nothing of ours around it until a session exists.
    const publicPath = computed(() => isPublicPath(path.value, APP_MODULES));
    const showChrome = computed(() => session.status === "ready");

    function onRoutingEvent(): void {
      path.value = window.location.pathname;
    }

    /**
     * 🔴 A module ASKING to assist (D34). The module never swaps the session itself — only the shell
     * holds the credential this tab signs in with, and a module that could set it is a module that
     * could sign anybody in. The token is verified by `establishSession` before anything is worn, and
     * the banner is read off its own claims.
     */
    async function onAssistRequest(event: Event): Promise<void> {
      const detail = (event as CustomEvent<HostAssistRequest>).detail;
      if (typeof detail?.token !== "string") return;
      if (await session.startAssisting(detail.token, detail.subjectName ?? null)) {
        session.noteAssistReason(typeof detail.reason === "string" ? detail.reason : "");
      }
    }

    // Re-read the URL on every status change as well: single-spa only emits
    // routing events once `singleSpa.start()` has run, and start() waits for
    // status "ready" (boot.ts) — a `history.replaceState` before that fires
    // neither `popstate` nor a routing event.
    watch(() => session.status, onRoutingEvent);
    // The bar's height is published only while the bar is there: a module mounted
    // signed-out on a public path lays itself out from the top edge, and drops
    // under the bar the moment the session it establishes draws one.
    watch(
      [showChrome, () => session.assisting !== null, mobile],
      ([withBar, assisting, isMobile]) => {
        const bar = withBar ? Number.parseInt(HEADER_HEIGHT_WITH_BAR, 10) : Number.parseInt(HEADER_HEIGHT_WITHOUT_BAR, 10);
        const banner = assisting ? ASSIST_BANNER_HEIGHT_PX : 0;
        document.documentElement.style.setProperty(ASSIST_BANNER_HEIGHT_VAR, `${banner}px`);
        document.documentElement.style.setProperty(HEADER_HEIGHT_VAR, `${bar + banner}px`);
        // The rail is drawn on exactly the same condition as the bar, so it is published here rather
        // than in a watcher of its own — one place decides whether this screen has chrome.
        // A phone has no rail (it lives in the nav drawer), so it reserves no strip at the foot.
        const rail = withBar && !isMobile ? RAIL_HEIGHT_WITH_RAIL : RAIL_HEIGHT_WITHOUT_RAIL;
        document.documentElement.style.setProperty(RAIL_HEIGHT_VAR, rail);
        // 🔴 **THE RAIL *IS* THE FOOTER, and saying otherwise cost every module its bottom edge** (D56).
        //
        // `--pinksy-footer-height` is the token eight modules and the kit lay themselves out by —
        // `height: calc(100% - var(--pinksy-footer-height))` — and it was published as a literal
        // "0px" in `onMounted`, from before the bottom rail existed (D44). So every module believed
        // it owned the screen to the bottom edge while a 26px FIXED rail sat on its last rows.
        //
        // Invisible on a scrolling screen, because you can scroll past the covered part. A
        // full-height grid cannot: its status bar — the row count — is pinned exactly where the rail
        // lands. That is what made a stale token look like "spacing around the grid".
        document.documentElement.style.setProperty(FOOTER_HEIGHT_VAR, withBar && !isMobile ? FOOTER_HEIGHT_WITH_RAIL : RAIL_HEIGHT_WITHOUT_RAIL);
      },
      { immediate: true },
    );

    onMounted(() => {
      window.addEventListener("popstate", onRoutingEvent);
      window.addEventListener("single-spa:before-routing-event", onRoutingEvent);
      window.addEventListener(HOST_ASSIST_EVENT, onAssistRequest);
    });
    onUnmounted(() => {
      window.removeEventListener("popstate", onRoutingEvent);
      window.removeEventListener("single-spa:before-routing-event", onRoutingEvent);
      window.removeEventListener(HOST_ASSIST_EVENT, onAssistRequest);
    });

    return { session, mobile, showAppHome, publicPath, resetToken, invitationToken, onboardStartToken, showSecurity, oauthAuthorize, oauthConsent };
  },
});

/** The width every hosted module offsets itself by. */
function publishNavWidth(width: string): void {
  document.documentElement.style.setProperty(NAV_WIDTH_VAR, width);
}
</script>
